import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Trans, useTranslation } from 'react-i18next';
import { AccountInfo } from '@azure/msal-browser';
import { acquireSignUpAccessToken, getMsalInstance } from '../auth/msal';
import { InvitationDetails, getInvitationDetails } from '../api/invitations';
import { triggerSignUp } from '../api/signUp';
import { resolveApiErrorMessage } from '../api/errorResponse';
import { getTaskState, TaskSeverity } from '../api/taskStates';
import { config } from '../config/env';
import { PageLayout } from '../components/PageLayout';
import './InitializationPage.scss';

const POLL_INTERVAL_MS = 1_000;

const isGuid = (value: string | null): value is string =>
  !!value && /^[\dA-Fa-f-]{36}$/.test(value);

const trimInnerWhitespace = (input: string) => input.replace(/\s+/g, ' ').trim();

type Phase =
  | 'loading'
  | 'awaiting-consent-propagation'
  | 'invalid-params'
  | 'verifying-tenant'
  | 'initializing'
  | 'success'
  | 'error';

interface ParsedSuccessMessage {
  title: string;
  tenantId?: string;
  secret?: string;
  raw?: string;
}

/**
 * On a hybrid-mode tenant the backend appends the tenant id and the hybrid
 * secret to the success message, separated by single '@' chars
 * (`"<name> is ready.@<tenantId>@<hybridSecret>"`, built in
 * `OrganizationConfigurator`). Split on '@' and only treat it as the
 * structured form when the last two parts are GUID-shaped, so a stray '@' in
 * the tenant name can't be mistaken for the separator.
 */
const parseFinishedMessage = (message: string): ParsedSuccessMessage => {
  const parts = message.split('@');
  if (parts.length === 3 && isGuid(parts[1].trim()) && isGuid(parts[2].trim())) {
    return {
      title: parts[0].trim(),
      tenantId: parts[1].trim(),
      secret: parts[2].trim(),
    };
  }
  return { title: '', raw: message };
};

const INVITATION_STORAGE_KEY = 'mnspro.signup.invitationId';
const TENANT_STORAGE_KEY = 'mnspro.signup.tenantId';

export const InitializationPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlInvitationId = searchParams.get('invitationId');
  const urlTenantId = searchParams.get('tenantId');

  // MSAL's loginRedirect round-trip lands the browser back on the
  // registered redirect URI without our query params. Persist the ids in
  // sessionStorage on first arrival and restore from there on subsequent
  // mounts so the params survive the MSAL detour. The invitation id is
  // persisted on its own too: on a fully-consented re-entry the SignUpPage
  // forwards here with only the invitation id (no tenant), and it still has
  // to survive the login redirect.
  useEffect(() => {
    if (isGuid(urlInvitationId)) {
      window.sessionStorage.setItem(INVITATION_STORAGE_KEY, urlInvitationId);
    }
    if (isGuid(urlTenantId)) {
      window.sessionStorage.setItem(TENANT_STORAGE_KEY, urlTenantId);
    }
  }, [urlInvitationId, urlTenantId]);

  const invitationId = urlInvitationId ?? window.sessionStorage.getItem(INVITATION_STORAGE_KEY);
  const paramTenantId = urlTenantId ?? window.sessionStorage.getItem(TENANT_STORAGE_KEY);

  const [phase, setPhase] = useState<Phase>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [tenantNameInput, setTenantNameInput] = useState('');
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [severity, setSeverity] = useState<TaskSeverity | null>(null);
  const [endTimeApprox, setEndTimeApprox] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [finishedDetails, setFinishedDetails] = useState<ParsedSuccessMessage | null>(null);
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  // The tenant we operate on must always match the signed-in account, so the
  // account's tenant id (`tid`) is the source of truth whenever an account is
  // present. This is what makes "change admin account" work: after switching
  // from user A to user B the tenant follows B instead of a stale value left
  // over from A. The param / sessionStorage value (carried from the AAD
  // consent callback) is only a bridge for the brief window before the
  // account has loaded — the details fetch never runs without an account.
  const tenantId =
    account && isGuid(account.tenantId)
      ? account.tenantId
      : paramTenantId && isGuid(paramTenantId)
      ? paramTenantId
      : null;

  // Persist the resolved tenant id so it survives a reload, and so a switched
  // account overwrites the previous account's tenant in storage.
  useEffect(() => {
    if (tenantId) {
      window.sessionStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    }
  }, [tenantId]);

  // Ensures we trigger `loginRedirect` at most once per browser visit.
  // Without this, a StrictMode double-invoke or an unrelated re-render
  // could fire two redirects in quick succession.
  const loginRedirectTriggered = useRef(false);

  // Step 1: Validate URL params and start MSAL sign-in. We need a bearer
  // token before calling /api/v2/Invitation/.../details — the backend
  // currently returns 401 on that endpoint despite [AllowAnonymous].
  //
  // `handleRedirectPromise` is called once in App.tsx during bootstrap, so
  // by the time we get here `getAllAccounts()` already reflects the
  // post-login state. We never call handleRedirectPromise from here — doing
  // so multiple times leads to no-op nulls that look like "not logged in"
  // and trigger spurious loginRedirect loops.
  useEffect(() => {
    // Only the invitation id is mandatory up front. The tenant id may still
    // be unknown here on a fully-consented re-entry — it is derived from the
    // MSAL account once the admin has signed in (see `tenantId` above).
    if (!isGuid(invitationId)) {
      setPhase('invalid-params');
      return;
    }

    const msal = getMsalInstance();
    if (!msal) {
      setPhase('error');
      setErrorMessage(t('signup:initialization:initializationPage:msal_not_initialized'));
      return;
    }

    const activeAccount = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
    if (activeAccount) {
      setAccount(activeAccount);
      return;
    }

    if (loginRedirectTriggered.current) return;
    loginRedirectTriggered.current = true;
    void msal.loginRedirect({
      scopes: [`${config.mainAppId}/.default`],
    });
  }, [invitationId, t]);

  // Step 2: Once we have an MSAL account, fetch invitation details with
  // bearer token. Retry every 5s while Azure AD is still propagating the
  // freshly granted consent (backend returns non-2xx in that window).
  //
  // `phase` deliberately NOT in deps — the catch branch sets phase to
  // 'awaiting-consent-propagation' and if the effect re-ran on that change
  // it would short-circuit the setTimeout and retry immediately, never
  // letting AAD catch up.
  useEffect(() => {
    if (!account || !invitationId || !tenantId) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const loadDetails = async () => {
      try {
        const token = await acquireSignUpAccessToken(account);
        const details = await getInvitationDetails(token, invitationId, tenantId);
        if (cancelled) return;
        setInvitation({
          ...details,
          tenantName: trimInnerWhitespace(details.tenantName),
        });
        setPhase('verifying-tenant');
      } catch {
        if (cancelled) return;
        setPhase('awaiting-consent-propagation');
        timeoutId = setTimeout(loadDetails, 5_000);
      }
    };

    void loadDetails();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [account, invitationId, tenantId]);

  const tenantNameMatches = useMemo(
    () =>
      !!invitation &&
      tenantNameInput.trim().length > 0 &&
      tenantNameInput === invitation.tenantName,
    [invitation, tenantNameInput],
  );

  // Polling loop while initializing.
  useEffect(() => {
    if (phase !== 'initializing') return;
    if (!account || !invitationId || !tenantId) return;

    let cancelled = false;

    const poll = async (token: string, organizationId: string, jobId: string) => {
      const state = await getTaskState(token, organizationId, jobId);
      if (cancelled) return;

      if ('notFound' in state) {
        setPhase('success');
        setFinishedDetails({
          title: t('signup:initialization:initializationPage:success_default_message'),
        });
        return;
      }

      const { percentDone, severity: taskSeverity, message, endTimeApprox: approxEnd } = state;
      setStatusMessage(message);
      setSeverity(taskSeverity);
      setEndTimeApprox(approxEnd ?? null);

      if (percentDone === 1) {
        setPhase('success');
        setFinishedDetails(parseFinishedMessage(message));
        return;
      }

      if (typeof percentDone === 'number' && percentDone > 0) {
        setProgress(percentDone);
      } else {
        setProgress(null);
      }

      // A 'Failed' severity is surfaced to the user (see the polling view),
      // but we deliberately keep polling — the backend job may recover and
      // continue, so a transient failure must not abort the flow.
      const shouldKeepPolling =
        percentDone === null ||
        percentDone === 0 ||
        taskSeverity === 'Pending' ||
        taskSeverity === 'Running' ||
        taskSeverity === 'Failed' ||
        (typeof percentDone === 'number' && percentDone < 1);

      if (shouldKeepPolling) {
        setTimeout(() => {
          void poll(token, organizationId, jobId);
        }, POLL_INTERVAL_MS);
      }
    };

    const start = async () => {
      try {
        const token = await acquireSignUpAccessToken(account);
        const { organizationId, jobId } = await triggerSignUp(token, invitationId, tenantId);
        setStatusMessage(t('signup:initialization:initializationPage:started'));
        setProgress(0);
        setTimeout(() => {
          void poll(token, organizationId, jobId);
        }, POLL_INTERVAL_MS);
      } catch (error) {
        if (cancelled) return;
        setPhase('error');
        setErrorMessage(
          resolveApiErrorMessage(
            error,
            t('signup:initialization:initializationPage:error_generic'),
          ),
        );
      }
    };

    void start();
    return () => {
      cancelled = true;
    };
  }, [phase, account, invitationId, tenantId, t]);

  // Tick once per second while initializing so the estimated-remaining
  // countdown derived from `endTimeApprox` stays live between polls.
  useEffect(() => {
    if (phase !== 'initializing') return;
    const intervalId = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(intervalId);
  }, [phase]);

  // Approximate remaining duration (end - now) as a localized string, or null
  // when no estimate is available or it has already elapsed.
  const remainingLabel = useMemo(() => {
    if (!endTimeApprox) return null;
    const endMs = new Date(endTimeApprox).getTime();
    if (Number.isNaN(endMs)) return null;
    const remainingMs = endMs - now;
    if (remainingMs <= 0) return null;

    const totalSeconds = Math.round(remainingMs / 1_000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const duration =
      minutes > 0
        ? t('signup:initialization:initializationPage:duration_minutes_seconds', {
            minutes,
            seconds,
          })
        : t('signup:initialization:initializationPage:duration_seconds', { seconds });
    return t('signup:initialization:initializationPage:eta', { duration });
  }, [endTimeApprox, now, t]);

  const handleCopySecret = () => {
    if (!finishedDetails?.secret) return;
    void navigator.clipboard
      .writeText(finishedDetails.secret)
      .then(() => {
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2_000);
      })
      .catch(() => {
        // Clipboard access can be denied (e.g. insecure context) — fail
        // silently; the admin can still reveal and copy the secret manually.
      });
  };

  const handleStartInitialization = () => {
    if (!invitation || !account || !tenantNameMatches) return;
    setPhase('initializing');
  };

  // "Change admin account": force the Microsoft account picker via
  // prompt=select_account instead of logging out (a plain logout strands the
  // admin on the home page). The chosen account comes back through the normal
  // login redirect and is made active in `msal.ts`, so it wins over any other
  // cached account. The invitation / tenant ids survive the round trip in
  // sessionStorage; the login redirect lands back on /initialization.
  const handleChangeAdminAccount = () => {
    const msal = getMsalInstance();
    if (!msal) return;
    // Pre-empt the login effect so it doesn't also fire a redirect.
    loginRedirectTriggered.current = true;
    void msal.loginRedirect({
      scopes: [`${config.mainAppId}/.default`],
      prompt: 'select_account',
    });
  };

  if (phase === 'invalid-params') {
    return (
      <PageLayout>
        <Typography variant="h5" gutterBottom>
          {t('signup:initialization:initializationPage:not_validated_title')}
        </Typography>
        <Typography>
          {t('signup:initialization:initializationPage:not_validated_description')}
        </Typography>
      </PageLayout>
    );
  }

  if (phase === 'awaiting-consent-propagation') {
    return (
      <PageLayout>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="h6">
            {t('signup:initialization:initializationPage:waiter_title')}
          </Typography>
          <Typography color="text.secondary" align="center">
            {t('signup:initialization:initializationPage:waiter_description')}
          </Typography>
        </Stack>
      </PageLayout>
    );
  }

  if (phase === 'loading' || phase === 'verifying-tenant') {
    if (!invitation) {
      return (
        <PageLayout>
          <CircularProgress />
        </PageLayout>
      );
    }
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="overline" color="text.secondary">
            {t('signup:verifyTenant:initializationPage:eyebrow')}
          </Typography>
          <Typography color="text.primary">
            <Trans
              i18nKey="signup:verifyTenant:initializationPage:description"
              values={{
                invitationName: invitation.invitationName,
                tenantName: invitation.tenantName,
                domains: invitation.domains.join(', '),
              }}
              components={{ b: <strong /> }}
            />
          </Typography>
          <Typography color="text.primary">
            <Trans
              i18nKey="signup:verifyTenant:initializationPage:instruction"
              values={{ tenantName: invitation.tenantName }}
              components={{ b: <strong /> }}
            />
          </Typography>
          <input
            className="tenant-verify-input"
            aria-label={t('signup:verifyTenant:initializationPage:input_label') ?? 'Tenant name'}
            value={tenantNameInput}
            onChange={(event) => setTenantNameInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && tenantNameMatches) {
                handleStartInitialization();
              }
            }}
            placeholder={invitation.tenantName}
          />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            <Button variant="outlined" color="primary" onClick={handleChangeAdminAccount} fullWidth>
              {t('signup:verifyTenant:initializationPage:button_change_account')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStartInitialization}
              disabled={!tenantNameMatches}
              fullWidth
            >
              {t('signup:verifyTenant:initializationPage:button_start')}
            </Button>
          </Stack>
        </Stack>
      </PageLayout>
    );
  }

  if (phase === 'initializing') {
    return (
      <PageLayout>
        <Stack spacing={2} alignItems="center">
          <Typography variant="overline" color="text.secondary">
            {t('signup:initialization:initializationPage:eyebrow')}
          </Typography>
          <Typography variant="h5">
            {t('signup:initialization:initializationPage:title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:initialization:initializationPage:description')}
          </Typography>
          <Box sx={{ width: '100%', my: 2 }}>
            <LinearProgress
              variant={
                progress !== null && progress > 0 && progress < 1 ? 'determinate' : 'indeterminate'
              }
              value={progress !== null && progress > 0 && progress < 1 ? progress * 100 : undefined}
            />
          </Box>
          {severity === 'Failed' ? (
            <Alert severity="error" sx={{ width: '100%' }}>
              <Typography variant="body2" component="div">
                {statusMessage || t('signup:initialization:initializationPage:status_fallback')}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                {t('signup:initialization:initializationPage:failed_retrying')}
              </Typography>
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {statusMessage || t('signup:initialization:initializationPage:status_fallback')}
            </Typography>
          )}
          {remainingLabel && (
            <Typography variant="body2" color="text.secondary">
              {remainingLabel}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('signup:initialization:initializationPage:dont_close')}
          </Typography>
        </Stack>
      </PageLayout>
    );
  }

  if (phase === 'success' && finishedDetails) {
    return (
      <PageLayout>
        <Stack spacing={2} alignItems="center">
          <CheckIcon color="success" sx={{ fontSize: 32 }} />
          <Typography variant="h5">
            {finishedDetails.title || t('signup:initialization:initializationPage:success_title')}
          </Typography>
          {finishedDetails.secret && (
            <Alert severity="info" sx={{ width: '100%' }}>
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  {t('signup:initialization:initializationPage:hybrid_secret_hint')}
                </Typography>
                {finishedDetails.tenantId && (
                  <Box>
                    <Typography variant="subtitle2">
                      {t('signup:initialization:initializationPage:tenant_id_label')}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {finishedDetails.tenantId}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2">
                    {t('signup:initialization:initializationPage:hybrid_secret_label')}
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      align="center"
                      sx={{ wordBreak: 'break-all', px: 9 }}
                    >
                      {secretRevealed
                        ? finishedDetails.secret
                        : '•'.repeat(finishedDetails.secret.length)}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      <Tooltip
                        title={
                          secretRevealed
                            ? t('signup:initialization:initializationPage:secret_hide')
                            : t('signup:initialization:initializationPage:secret_show')
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => setSecretRevealed((prev) => !prev)}
                          aria-label={
                            secretRevealed
                              ? t('signup:initialization:initializationPage:secret_hide') ??
                                'Hide secret'
                              : t('signup:initialization:initializationPage:secret_show') ??
                                'Show secret'
                          }
                        >
                          {secretRevealed ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          secretCopied
                            ? t('signup:initialization:initializationPage:secret_copied')
                            : t('signup:initialization:initializationPage:secret_copy')
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={handleCopySecret}
                          aria-label={
                            t('signup:initialization:initializationPage:secret_copy') ??
                            'Copy secret'
                          }
                        >
                          {secretCopied ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : (
                            <ContentCopyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Box>
              </Stack>
            </Alert>
          )}
          {finishedDetails.raw && (
            <Typography color="text.secondary">{finishedDetails.raw}</Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            href={config.webAppUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t('signup:initialization:initializationPage:button_start_mnsprocloud')}
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Stack spacing={2} alignItems="center">
        <ErrorIcon color="error" sx={{ fontSize: 32 }} />
        <Typography variant="h5">
          {t('signup:initialization:initializationPage:error_title')}
        </Typography>
        <Alert severity="error" sx={{ width: '100%' }}>
          {errorMessage || t('signup:initialization:initializationPage:error_generic')}
        </Alert>
      </Stack>
    </PageLayout>
  );
};
